import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconInfoCircle, IconLoader2, IconSend, IconX } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/lib/api';
import { MAX_MESSAGE_LENGTH } from '../pages/CampaignsPage';

interface TargetedSendMvnoOption {
  id: string;
  label: string;
}

interface TargetedSendCardProps {
  availableMvnos: TargetedSendMvnoOption[];
}

// Duplicated rather than imported (see CampaignJobsPage.tsx for the same
// inline copy) — strata-portal's convention for this small, dependency-free
// error-shape reader is per-file, not a shared module.
const getApiErrorMessage = (err: any, fallback: string): string => (
  err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || fallback
);

const NONE_MVNO_SENTINEL = 'none';
const MSISDN_PATTERN = /^27\d{9}$/;
const CONFIRM_PREVIEW_RECIPIENT_COUNT = 5;

/**
 * Normalizes one MSISDN token to bare-27 form. Only the three canonical South
 * African shapes are recognised (+27XXXXXXXXX, 0XXXXXXXXX, 27XXXXXXXXX) —
 * anything else is returned unchanged so MSISDN_PATTERN rejects it as invalid
 * instead of a blanket digit-strip silently turning garbage input (e.g. a
 * mispasted reference number) into something that looks like a valid MSISDN.
 */
function normalizeMsisdn(raw: string): string {
  if (/^\+27\d{9}$/.test(raw)) return raw.slice(1);
  if (/^0\d{9}$/.test(raw)) return `27${raw.slice(1)}`;
  return raw;
}

/** Splits on newline/comma/space, normalizes, and dedupes valid MSISDNs. */
function parseMsisdns(raw: string): { valid: string[]; invalidCount: number } {
  const tokens = raw
    .split(/[\n,\s]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);

  const validSet = new Set<string>();
  let invalidCount = 0;
  for (const token of tokens) {
    const normalized = normalizeMsisdn(token);
    if (MSISDN_PATTERN.test(normalized)) {
      validSet.add(normalized);
    } else {
      invalidCount += 1;
    }
  }
  return { valid: [...validSet], invalidCount };
}

/**
 * Sends an SMS to a hand-entered list of MSISDNs — no Nova CDC audience query
 * involved. Mounted below the Campaign Audience card on CampaignsPage; reuses
 * that page's already-fetched availableMvnos instead of loading its own copy.
 */
export function TargetedSendCard({ availableMvnos }: TargetedSendCardProps) {
  const [msisdnInput, setMsisdnInput] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [message, setMessage] = useState('');
  const [mvnoId, setMvnoId] = useState(NONE_MVNO_SENTINEL);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<{ jobId: string; queued: number; campaignId: string } | null>(null);

  const { valid: validMsisdns, invalidCount } = useMemo(() => parseMsisdns(msisdnInput), [msisdnInput]);
  const effectiveMvnoId = mvnoId === NONE_MVNO_SENTINEL ? undefined : mvnoId;
  const campaignIdPreview = `strata:${effectiveMvnoId || 'legacy'}:${campaignName.trim() || '<campaign name>'}`;

  const canSend =
    invalidCount === 0 &&
    validMsisdns.length > 0 &&
    campaignName.trim().length > 0 &&
    message.trim().length > 0;

  const resetForm = () => {
    setMsisdnInput('');
    setCampaignName('');
    setMessage('');
    setMvnoId(NONE_MVNO_SENTINEL);
  };

  const handleSendClick = () => {
    setError('');
    if (!canSend) return;
    setConfirmOpen(true);
  };

  const handleConfirmSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    setError('');
    setSuccess(null);
    try {
      const response = await api.post('/campaigns/send-sms', {
        users: validMsisdns.map((msisdn) => ({ msisdn })),
        message: message.trim(),
        campaignName: campaignName.trim(),
        mvnoId: effectiveMvnoId,
      });
      if (response.data.success) {
        setSuccess({
          jobId: response.data.jobId,
          queued: response.data.queued || 0,
          campaignId: response.data.campaignId,
        });
        resetForm();
      } else {
        setError('Failed to queue targeted SMS send');
      }
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to queue targeted SMS send'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Card className="mb-6">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-1">Targeted Send</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Send an SMS to a hand-entered list of MSISDNs instead of a full Nova CDC audience.
            No dynamic fields (e.g. first name or brand) — hand-entered recipients have no
            spend or profile data to fill them with.
          </p>

          <div className="grid gap-2 max-w-xl mb-6">
            <Label>Recipient MSISDNs</Label>
            <Textarea
              rows={5}
              value={msisdnInput}
              onChange={(e) => setMsisdnInput(e.target.value)}
              placeholder={'One per line, or separated by commas/spaces, e.g.\n0821234567\n+27829876543\n27831112222'}
              disabled={sending}
            />
            <p className="text-xs text-muted-foreground">
              {validMsisdns.length} valid · {invalidCount} invalid
            </p>
          </div>

          <div className="grid gap-2 max-w-xl mb-6">
            <Label>Campaign Name</Label>
            <Input
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="Enter campaign name"
              disabled={sending}
            />
          </div>

          <div className="grid gap-2 max-w-xl mb-6">
            <Label>Message</Label>
            <Textarea
              rows={3}
              value={message}
              onChange={(e) => {
                if (e.target.value.length <= MAX_MESSAGE_LENGTH) setMessage(e.target.value);
              }}
              placeholder="Enter your SMS message here"
              maxLength={MAX_MESSAGE_LENGTH}
              disabled={sending}
            />
            <p className="text-xs text-muted-foreground">
              {message.length}/{MAX_MESSAGE_LENGTH} characters
            </p>
          </div>

          <div className="grid gap-2 max-w-xl mb-6">
            <Label>Nova MVNO (optional)</Label>
            <Select value={mvnoId} onValueChange={(value) => setMvnoId(value || NONE_MVNO_SENTINEL)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="None (legacy campaign id)" />
              </SelectTrigger>
              <SelectContent className="w-[var(--radix-select-trigger-width)] min-w-72">
                <SelectItem value={NONE_MVNO_SENTINEL}>None</SelectItem>
                {availableMvnos.map((mvno) => (
                  <SelectItem key={mvno.id} value={mvno.id}>
                    {mvno.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <IconInfoCircle size={14} />
              Campaign id will be <code className="font-mono">{campaignIdPreview}</code>
            </p>
          </div>

          <Button variant="default" onClick={handleSendClick} disabled={!canSend || sending}>
            <IconSend size={16} />
            {`Send Targeted SMS${validMsisdns.length > 0 ? ` (${validMsisdns.length})` : ''}`}
          </Button>

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription className="flex items-center justify-between">
                {error}
                <Button variant="ghost" size="icon-xs" onClick={() => setError('')}><IconX size={14} /></Button>
              </AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert variant="success" className="mt-4">
              <AlertDescription className="flex items-center justify-between">
                <span>
                  Queued {success.queued} SMS for durable delivery under campaign{' '}
                  <code className="font-mono">{success.campaignId}</code>. Job: {success.jobId}.{' '}
                  <Link to="/campaigns/jobs" className="underline">
                    View in Send Jobs
                  </Link>
                </span>
                <Button variant="ghost" size="icon-xs" onClick={() => setSuccess(null)}><IconX size={14} /></Button>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={(open) => { if (!open && !sending) setConfirmOpen(false); }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Confirm Targeted SMS Send</DialogTitle>
            <DialogDescription>
              Are you sure you want to send this SMS to the recipients below?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <div>
                <p className="text-sm font-medium">Campaign Name:</p>
                <p className="text-sm">{campaignName.trim()}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Campaign Id:</p>
                <p className="text-sm font-mono">{campaignIdPreview}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Recipients:</p>
                <p className="text-sm">
                  {validMsisdns.length} MSISDN{validMsisdns.length !== 1 ? 's' : ''}:{' '}
                  {validMsisdns.slice(0, CONFIRM_PREVIEW_RECIPIENT_COUNT).join(', ')}
                  {validMsisdns.length > CONFIRM_PREVIEW_RECIPIENT_COUNT
                    ? ` +${validMsisdns.length - CONFIRM_PREVIEW_RECIPIENT_COUNT} more`
                    : ''}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium">Message:</p>
                <p className="text-sm whitespace-pre-wrap">{message.trim()}</p>
              </div>
            </div>
            <Alert variant="warning">
              <AlertDescription>
                This action cannot be undone. The SMS will be sent to all {validMsisdns.length}{' '}
                recipients.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending}>
              Cancel
            </Button>
            <Button variant="default" onClick={handleConfirmSend} disabled={sending}>
              {sending ? <IconLoader2 size={16} className="animate-spin" /> : <IconSend size={16} />}
              {sending ? 'Sending...' : 'Confirm & Send SMS'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
